import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Card } from '@/components/atoms/Card';
import Checkbox from '@/components/atoms/Checkbox';

import styles from '../actionModals.module.scss';

interface BioscopesSectionProps {
    sourceCageName: string;
    destinationCageName: string;
    sourceBioscopes: number[];
    /** `null` when the operator hasn't picked a target cage yet. */
    targetBioscopes: number[] | null;
    selectedBioscopeIds: Set<number>;
    onToggle: (bioscopeId: number) => void;
    /** When false the checkboxes render disabled and a tooltip explains why. */
    canMove: boolean;
}

/**
 * Optional bioscope companion-move picker. Destination-centric: the operator
 * sees what is currently at the chosen target cage (or a hint while none is
 * picked) and selects from the source cage's bioscopes to bring along. Moves
 * are atomic with the relocation — handled by the backend in one transaction.
 */
export function RelocateBioscopesSection(props: BioscopesSectionProps) {
    const {
        sourceCageName,
        destinationCageName,
        sourceBioscopes,
        targetBioscopes,
        selectedBioscopeIds,
        onToggle,
        canMove,
    } = props;
    const { t } = useTranslation('fishGroups');

    const targetContextLabel =
        targetBioscopes && destinationCageName
            ? targetBioscopes.length === 0
                ? t('actions.relocate.bioscopes.destinationContextEmpty', {
                      cageName: destinationCageName,
                  })
                : t('actions.relocate.bioscopes.destinationContextPresent', {
                      cageName: destinationCageName,
                      ids: targetBioscopes.join(', '),
                  })
            : null;

    return (
        <Card
            as="section"
            radius="sm"
            variant="subtle"
            padding="xs"
            className={styles.bioscopesSection}>
            <h5 className={styles.bioscopesHeading}>{t('actions.relocate.bioscopes.heading')}</h5>
            <p className={styles.bioscopesDescription}>
                {t('actions.relocate.bioscopes.description')}
            </p>
            {targetContextLabel && <p className={styles.bioscopesContext}>{targetContextLabel}</p>}
            <span className={styles.label}>
                {destinationCageName
                    ? t('actions.relocate.bioscopes.sourceLabel', {
                          cageName: sourceCageName,
                          destCageName: destinationCageName,
                      })
                    : t('actions.relocate.bioscopes.sourceLabel', {
                          cageName: sourceCageName,
                          destCageName: '…',
                      })}
            </span>
            <ul className={styles.bioscopesList}>
                {sourceBioscopes.map((bioscopeId) => (
                    <li
                        key={bioscopeId}
                        className={classNames(styles.bioscopeItem, {
                            [styles['bioscopeItem--disabled']]: !canMove,
                        })}
                        title={
                            canMove
                                ? undefined
                                : t('actions.relocate.bioscopes.noPermissionTooltip')
                        }>
                        <Checkbox
                            label={t('actions.relocate.bioscopes.sourceOption', { id: bioscopeId })}
                            checked={selectedBioscopeIds.has(bioscopeId)}
                            disabled={!canMove}
                            onCheckedChange={() => onToggle(bioscopeId)}
                        />
                    </li>
                ))}
            </ul>
        </Card>
    );
}
